import React from 'react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { Play, Pause, X, SpeakerHigh, SpeakerLow, SpeakerX, WarningCircle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const AudioPlayer = () => {
  const { currentStation, isPlaying, volume, loading, error, togglePlay, stopPlayer, setVolume } = useAudioPlayer();

  if (!currentStation) return null;

  const VolumeIcon = volume === 0 ? SpeakerX : volume < 0.5 ? SpeakerLow : SpeakerHigh;

  return (
    <div
      data-testid="audio-player-bar"
      className="fixed bottom-16 md:bottom-0 left-0 right-0 z-40 backdrop-filter backdrop-blur-2xl bg-[#050505]/90 border-t border-white/10 px-4 md:px-6 py-3"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-4">
        {/* Station Info */}
        <Link
          to={`/station/${currentStation.station_id}`}
          className="flex items-center gap-3 min-w-0 flex-1 md:flex-initial md:w-80 hover:opacity-80 transition-opacity"
        >
          <img
            src={currentStation.logo_url || 'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=100'}
            alt={currentStation.name}
            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold text-white truncate" data-testid="player-station-name">{currentStation.name}</h4>
            <p className="text-xs text-gray-400 truncate">{currentStation.country} • {currentStation.genre}</p>
          </div>
        </Link>

        {/* Play Controls */}
        <div className="flex items-center gap-3 flex-1 justify-center">
          <button
            data-testid="player-toggle-btn"
            onClick={togglePlay}
            disabled={loading}
            className="w-12 h-12 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black flex items-center justify-center hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <Pause size={20} weight="fill" />
            ) : (
              <Play size={20} weight="fill" />
            )}
          </button>

          {/* Status */}
          <div className="hidden sm:flex items-center gap-2 text-xs">
            {error ? (
              <span className="text-red-400 flex items-center gap-1">
                <WarningCircle size={14} weight="fill" />
                {error}
              </span>
            ) : loading ? (
              <span className="text-cyan-400 animate-pulse">Loading stream...</span>
            ) : isPlaying ? (
              <span className="flex items-center gap-1 text-cyan-400">
                <span className="inline-flex gap-0.5">
                  <span className="w-1 h-3 bg-cyan-400 rounded animate-pulse" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1 h-3 bg-cyan-400 rounded animate-pulse" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-1 h-3 bg-cyan-400 rounded animate-pulse" style={{ animationDelay: '300ms' }}></span>
                </span>
                LIVE
              </span>
            ) : (
              <span className="text-gray-500">Paused</span>
            )}
          </div>
        </div>

        {/* Volume Control */}
        <div className="hidden md:flex items-center gap-2 w-40 mr-40">
          <VolumeIcon size={20} className="text-gray-400 flex-shrink-0" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            data-testid="volume-slider"
            className="flex-1 h-1 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-400 [&::-webkit-slider-thumb]:cursor-pointer"
            style={{
              background: `linear-gradient(to right, #00FFFF 0%, #00FFFF ${volume * 100}%, rgba(255,255,255,0.1) ${volume * 100}%, rgba(255,255,255,0.1) 100%)`
            }}
          />
        </div>

        {/* Close Button */}
        <button
          data-testid="player-close-btn"
          onClick={stopPlayer}
          className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
};

export default AudioPlayer;
